import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Language } from '@forest/contracts';

type Theme = 'light' | 'dark';

type UiState = {
  language: Language;
  theme: Theme;
  sidebarCollapsed: boolean;
  toasts: Toast[];
  setLanguage: (l: Language) => void;
  toggleTheme: () => void;
  toggleSidebar: () => void;
  pushToast: (t: Omit<Toast, 'id'>) => void;
  dismissToast: (id: string) => void;
};

export type Toast = {
  id: string;
  title?: string;
  message: string;
  variant?: 'success' | 'error' | 'info';
};

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      language: 'ro',
      theme: 'light',
      sidebarCollapsed: false,
      toasts: [],
      setLanguage: (language) => set({ language }),
      toggleTheme: () =>
        set((s) => {
          const theme = s.theme === 'light' ? 'dark' : 'light';
          if (typeof document !== 'undefined') {
            document.documentElement.classList.toggle('dark', theme === 'dark');
          }
          return { theme };
        }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      pushToast: (t) => {
        const id = Math.random().toString(36).slice(2);
        set((s) => ({ toasts: [...s.toasts, { ...t, id }] }));
        setTimeout(() => {
          set((s) => ({ toasts: s.toasts.filter((toast) => toast.id !== id) }));
        }, 10_000);
      },
      dismissToast: (id) =>
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
    }),
    {
      name: 'forest-ui',
      partialize: (s) => ({ language: s.language, theme: s.theme, sidebarCollapsed: s.sidebarCollapsed }),
      // After Zustand reads the persisted state on page load, mirror
      // the theme onto the <html> element so any state-driven re-paint
      // matches the FOUC-guard script in index.html. Without this hook,
      // a manual `useUiStore.setState({ theme })` call wouldn't update
      // the DOM until the next toggle.
      onRehydrateStorage: () => (state) => {
        if (typeof document === 'undefined') return;
        const theme = state?.theme ?? 'light';
        document.documentElement.classList.toggle('dark', theme === 'dark');
      },
    },
  ),
);
